import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { stripePromise } from "./stripe";

/* ================= PAYMENT FORM ================= */

function PaymentForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handlePay = async () => {
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMsg("");

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success.html`,
      },
    });

    if (error) setErrorMsg(error.message);
    setLoading(false);
  };

  return (
    <div className="space-y-5">
      <div className="p-4 border rounded-xl bg-slate-50">
        <PaymentElement />
      </div>

      {errorMsg && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
          {errorMsg}
        </div>
      )}

      <button
        onClick={handlePay}
        disabled={loading}
        className={`w-full py-3 rounded-lg font-semibold transition
        ${
          loading
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-emerald-600 text-white hover:bg-emerald-700"
        }`}
      >
        {loading ? "Processing Payment..." : "Pay Securely"}
      </button>

      <p className="text-xs text-gray-400 text-center">
        🔒 Secure payment powered by Stripe (PCI-DSS compliant)
      </p>
    </div>
  );
}

/* ================= STRIPE PAGE ================= */

export default function StripePayment() {
  const [clientSecret, setClientSecret] = useState(null);
  const [payment, setPayment] = useState(null);
  const [currency, setCurrency] = useState("USD");

  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (!token) return;

    // 1️⃣ Fetch payment details (READ-ONLY)
    axios
      .get(`https://booktrip4me.com/api/payment-details.php?token=${token}`)
      .then((res) => {
        setPayment(res.data);
        setCurrency(res.data.currency || "USD");
      })
      .catch(() => alert("Invalid or expired payment link"));

    // 2️⃣ Create Stripe PaymentIntent (backend decides amount + currency)
    axios
      .post(
        "https://booktrip4me.com/api/create-stripe-intent.php",
        { token },
        { headers: { "Content-Type": "application/json" } }
      )
      .then((res) => {
        setClientSecret(res.data.clientSecret);
        if (res.data.currency) {
          setCurrency(res.data.currency);
        }
      })
      .catch(() => alert("Failed to initialize payment"));
  }, [token]);

  const formatAmount = (amount, currency) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);

  if (!clientSecret || !payment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Initializing secure payment…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-slate-200">

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b text-center">
          <h2 className="text-2xl font-semibold text-emerald-600">
            Secure Payment
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Complete your payment safely
          </p>
        </div>

        {/* READ-ONLY SUMMARY */}
        <div className="px-6 py-4 bg-slate-50 border-b text-sm">
          <p>
            <span className="text-gray-500">Booking:</span>{" "}
            <b>{payment.booking_number}</b>
          </p>
          <p>
            <span className="text-gray-500">Passenger:</span>{" "}
            <b>{payment.customer_name}</b>
          </p>
          <p className="mt-2 text-lg font-semibold">
            Amount: {formatAmount(Number(payment.amount), currency)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Currency: {currency}
          </p>
        </div>

        {/* STRIPE ELEMENTS */}
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <div className="px-6 py-6">
            <PaymentForm />
          </div>
        </Elements>
      </div>
    </div>
  );
}
